import { supabase } from '../lib/supabase'
import { resolveAssistanceFormKind } from '../types/supportCenterSpecialties'
import type {
  SupportCenterAssistance,
  SupportCenterAssistanceInput,
  SupportCenterAssistanceRef,
  SupportCenterCoverageScope,
} from '../types/supportCenterAssistances'
import type { SupportCenterFormRef } from '../types/supportCenterForms'
import { nowTimestampSaoPaulo } from '../utils/dateOnly'

function mapCoverageScope(value: unknown): SupportCenterCoverageScope {
  return value === 'dependentes' ? 'dependentes' : 'titular'
}

function mapSpecificForm(raw: unknown): SupportCenterFormRef | null {
  const row = Array.isArray(raw) ? raw[0] : raw
  if (!row || typeof row !== 'object') return null
  const form = row as {
    name?: string | null
    form_key?: string | null
    table_name?: string | null
    triage_table_name?: string | null
    deleted_at?: string | null
  }
  const form_key = String(form.form_key ?? '').trim()
  if (!form_key || form.deleted_at) return null
  return {
    form_key,
    name: String(form.name ?? '').trim(),
    table_name: String(form.table_name ?? '').trim(),
    triage_table_name: String(form.triage_table_name ?? '').trim(),
  }
}

function mapAssistance(row: Record<string, unknown>): SupportCenterAssistance {
  const specificForm = mapSpecificForm(row.specific_form)
  const specificFormKey =
    specificForm?.form_key ??
    (typeof row.specific_form_key === 'string' ? row.specific_form_key.trim() : null) ??
    null
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    code: String(row.code ?? ''),
    form_kind: resolveAssistanceFormKind(specificFormKey),
    specific_form_key: specificFormKey || null,
    specific_form: specificForm,
    coverage_scope: mapCoverageScope(row.coverage_scope),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    deleted_at: (row.deleted_at as string | null) ?? null,
    deleted_by: (row.deleted_by as string | null) ?? null,
    deleted_reason: (row.deleted_reason as string | null) ?? null,
  }
}

function sortByCodeName(rows: SupportCenterAssistance[]): SupportCenterAssistance[] {
  return [...rows].sort((a, b) => {
    const byCode = a.code.localeCompare(b.code, 'pt-BR', { sensitivity: 'base' })
    if (byCode !== 0) return byCode
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  })
}

export const supportCenterAssistancesService = {
  async list(): Promise<SupportCenterAssistance[]> {
    const { data, error } = await supabase
      .from('support_center_assistances')
      .select(`
        *,
        specific_form:support_center_forms!specific_form_key(
          form_key, name, table_name, triage_table_name, deleted_at
        )
      `)
      .is('deleted_at', null)
      .order('code', { ascending: true })

    if (error) throw error
    return sortByCodeName((data ?? []).map((row) => mapAssistance(row as Record<string, unknown>)))
  },

  async upsert(input: SupportCenterAssistanceInput): Promise<string> {
    const name = input.name.trim()
    const code = input.code.trim()
    if (!name) throw new Error('Informe o nome da assistência.')
    if (!code) throw new Error('Informe o código da assistência.')
    if (input.coverage_scope !== 'titular' && input.coverage_scope !== 'dependentes') {
      throw new Error('Informe a cobertura.')
    }

    const specific_form_key = input.specific_form_key.trim() || null

    const payload = {
      name,
      code,
      specific_form_key,
      coverage_scope: input.coverage_scope,
    }

    if (input.id) {
      const { error } = await supabase
        .from('support_center_assistances')
        .update(payload)
        .eq('id', input.id)
        .is('deleted_at', null)
      if (error) throw error
      return input.id
    }

    const { data, error } = await supabase
      .from('support_center_assistances')
      .insert(payload)
      .select('id')
      .single()
    if (error) throw error
    return String(data.id)
  },

  async softDelete(id: string, reason: string, userId: string): Promise<void> {
    const deleted_reason = reason.trim()
    if (!deleted_reason) throw new Error('Informe o motivo da exclusão.')

    const { error } = await supabase
      .from('support_center_assistances')
      .update({
        deleted_at: nowTimestampSaoPaulo(),
        deleted_by: userId,
        deleted_reason,
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  /** Assistências ativas vinculadas à especialidade (ordenadas por code, name). */
  async listBySpecialization(specializationId: string): Promise<SupportCenterAssistanceRef[]> {
    if (!specializationId) return []

    const { data, error } = await supabase
      .from('support_center_specialty_assistances')
      .select(`
        assistance_id,
        assistance:support_center_assistances!assistance_id(id, name, code, deleted_at)
      `)
      .eq('specialty_id', specializationId)
      .is('deleted_at', null)

    if (error) throw error

    const refs: SupportCenterAssistanceRef[] = []
    for (const row of data ?? []) {
      const raw = row.assistance
      const assistance = Array.isArray(raw) ? raw[0] : raw
      if (!assistance || typeof assistance !== 'object') continue
      const a = assistance as {
        id?: string
        name?: string | null
        code?: string | null
        deleted_at?: string | null
      }
      if (!a.id || a.deleted_at) continue
      refs.push({
        id: a.id,
        name: (a.name ?? '').trim(),
        code: (a.code ?? '').trim(),
      })
    }

    return refs.sort((a, b) => {
      const byCode = a.code.localeCompare(b.code, 'pt-BR', { sensitivity: 'base' })
      if (byCode !== 0) return byCode
      return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
    })
  },

  /** Primeira assistência da especialidade (code, name) — regra do agendamento. */
  async getFirstForSpecialization(specializationId: string): Promise<SupportCenterAssistanceRef | null> {
    const list = await this.listBySpecialization(specializationId)
    return list[0] ?? null
  },

  async listIdsBySpecialization(specializationId: string): Promise<string[]> {
    const list = await this.listBySpecialization(specializationId)
    return list.map((item) => item.id)
  },

  /**
   * Assistência ativa pelo código (case-insensitive).
   * Retorna id, code e coverage_scope.
   */
  async getActiveByCode(
    code: string
  ): Promise<Pick<SupportCenterAssistance, 'id' | 'code' | 'coverage_scope'> | null> {
    const trimmed = code.trim()
    if (!trimmed) return null

    const { data, error } = await supabase
      .from('support_center_assistances')
      .select('id, code, coverage_scope')
      .is('deleted_at', null)

    if (error) throw error

    const needle = trimmed.toLocaleLowerCase('pt-BR')
    const row = (data ?? []).find(
      (item) =>
        String((item as { code?: string }).code ?? '').trim().toLocaleLowerCase('pt-BR') === needle
    ) as { id?: string; code?: string; coverage_scope?: string } | undefined

    if (!row?.id) return null
    return {
      id: String(row.id),
      code: String(row.code ?? '').trim(),
      coverage_scope: mapCoverageScope(row.coverage_scope),
    }
  },

  /**
   * Especialidades ativas vinculadas à assistência pelo código (N:N + code).
   */
  async listSpecializationIdsByAssistanceCode(code: string): Promise<string[]> {
    const assistance = await this.getActiveByCode(code)
    if (!assistance) return []

    const { data: links, error: linksError } = await supabase
      .from('support_center_specialty_assistances')
      .select('specialty_id')
      .eq('assistance_id', assistance.id)
      .is('deleted_at', null)

    if (linksError) throw linksError

    return [
      ...new Set(
        (links ?? [])
          .map((row) => String((row as { specialty_id?: string }).specialty_id ?? '').trim())
          .filter(Boolean)
      ),
    ]
  },

  /**
   * Sincroniza vínculos N:N da especialidade.
   * Soft-delete dos removidos; insert dos novos.
   */
  async syncSpecializationAssistances(
    specializationId: string,
    assistanceIds: string[],
    actorId: string | null
  ): Promise<void> {
    const desired = [...new Set(assistanceIds.filter(Boolean))]
    if (desired.length === 0) {
      throw new Error('Vincule ao menos uma assistência.')
    }

    const { data, error } = await supabase
      .from('support_center_specialty_assistances')
      .select('id, assistance_id')
      .eq('specialty_id', specializationId)
      .is('deleted_at', null)

    if (error) throw error

    const current = (data ?? []) as Array<{ id: string; assistance_id: string }>
    const currentById = new Map(current.map((row) => [row.assistance_id, row.id]))
    const desiredSet = new Set(desired)

    const toAdd = desired.filter((id) => !currentById.has(id))
    const toRemove = current.filter((row) => !desiredSet.has(row.assistance_id))

    if (toAdd.length > 0) {
      const { error: insertError } = await supabase
        .from('support_center_specialty_assistances')
        .insert(
          toAdd.map((assistanceId) => ({
            specialty_id: specializationId,
            assistance_id: assistanceId,
            created_by: actorId,
          }))
        )
      if (insertError) throw insertError
    }

    for (const row of toRemove) {
      const { error: removeError } = await supabase
        .from('support_center_specialty_assistances')
        .update({
          deleted_at: nowTimestampSaoPaulo(),
          deleted_by: actorId,
          deleted_reason: 'Removido na edição das assistências da especialidade',
        })
        .eq('id', row.id)
        .is('deleted_at', null)
      if (removeError) throw removeError
    }
  },
}
